import { hasListingOwnerIntent } from 'api/utils/pendingCreateAdFlow'
import PageLoading from 'components/common/loading/PageLoading'
import { useAuth } from 'pages/authenticated/AuthContext'
import { usePermissions } from 'pages/authenticated/PermissionsContext'
import { ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

type RequireOwnerEmailVerifiedProps = {
  children: ReactNode
}

type GateState = {
  synced: boolean
  needsVerify: boolean
}

/**
 * Publishers (owner role, owned listing, or active create-ad intent) must
 * confirm email before Mon espace / listing management.
 */
const RequireOwnerEmailVerified = ({ children }: RequireOwnerEmailVerifiedProps) => {
  const { isAuthenticated } = useAuth()
  const { role, emailValidated, hasListing, refresh } = usePermissions()
  const location = useLocation()
  const [gate, setGate] = useState<GateState>({
    synced: false,
    needsVerify: false,
  })

  useEffect(() => {
    if (!isAuthenticated) {
      setGate({ synced: true, needsVerify: false })
      return
    }
    // Re-sync on navigation without tying render to permissions `loading` —
    // that flag flips on every refresh and was remounting /account in a loop.
    setGate(prev => ({ ...prev, synced: false }))
    let cancelled = false
    void (async () => {
      const payload = await refresh(
        hasListingOwnerIntent() ? { intent: 'list_property' } : undefined
      )
      if (cancelled) return

      const isPublisher =
        payload?.role === 'owner' ||
        role === 'owner' ||
        Boolean(payload?.hasListing) ||
        hasListing ||
        hasListingOwnerIntent()

      const validated = Boolean(payload?.emailValidated ?? emailValidated)
      setGate({
        synced: true,
        needsVerify: isPublisher && !validated,
      })
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, location.pathname, location.search])

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: location }} />
  }

  if (!gate.synced) return <PageLoading />

  if (gate.needsVerify) {
    return (
      <Navigate
        to="/verify-email"
        replace
        state={{
          from: location,
          next: `${location.pathname}${location.search}${location.hash}`,
        }}
      />
    )
  }

  return <>{children}</>
}

export default RequireOwnerEmailVerified
